import { Edit2, FlaskConical, Send } from 'lucide-react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import {
  QA_STATUSES,
  QA_CATEGORIES,
  MAX_COMMENTS_PER_CALL,
} from '@intone/shared';

import { useImportConversationFromQA } from '@/hooks/useSimulations';
import { useIsSuperadmin } from '@/hooks/useIsSuperadmin';
import { useUserData } from '@/entities/user';
import { formatISODate, formatDate } from '@/shared/lib/date';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from "@/components/ui/card.tsx";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Textarea } from "@/components/ui/textarea.tsx";

import { useQAFeedbackSection } from '../qaFeedback';
import { useQACommentSection } from '../qaComment';

export function ConversationQASection({ conversationId }: { conversationId: string }) {
  const { userId } = useUserData();

  const {
    category,
    error,
    isCreateTicketPending,
    isFeedbackPending,
    isTicketCreated,
    qaFeedback,
    status,
    handleCreateTicket,
    handleStatusChange,
    handleCategoryChange,
  } = useQAFeedbackSection({
    entityId: conversationId,
    entityType: 'conversation',
    userId,
  });
  const {
    addRef,
    canAddComment,
    comments,
    editingId,
    editRef,
    hasComment,
    isAddCommentPending,
    isCommentPending,
    setHasComment,
    handleAddComment,
    handleCancelEdit,
    handleStartEdit,
    handleUpdateComment,
  } = useQACommentSection({
    entityId: conversationId,
    entityType: 'conversation',
    userId,
  });

  const importFromQA = useImportConversationFromQA();
  const navigate = useNavigate();
  const { isSuperadmin } = useIsSuperadmin();

  const isTicketPending = isFeedbackPending || isAddCommentPending;

  const handleImportAsTest = async () => {
    try {
      const test = await importFromQA.mutateAsync(conversationId)
      toast.success('Test case created from QA')
      navigate(`/simulations?test_id=${test.id}`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to import test')
    }
  }

  return (
    <div className="grid gap-4">
      <div>
        <h3 className="font-medium text-heading">Manual QA</h3>

        {qaFeedback && (
          <div className="text-detail text-gray-500">
            Last updated: {formatISODate(qaFeedback.updated_time)}
          </div>
        )}
      </div>

      <div className="grid gap-3">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-2 gap-3">
          <label className="text-detail font-medium text-gray-700 grid gap-1">
            Status:
            <Select
              value={status}
              onValueChange={handleStatusChange}
              disabled={isTicketPending}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select status"/>
              </SelectTrigger>
              <SelectContent>
                {Object.values(QA_STATUSES).map((value) => (
                  <SelectItem key={value} value={value}>
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <label className="text-detail font-medium text-gray-700 grid gap-1">
            Category:
            <Select
              value={category}
              onValueChange={handleCategoryChange}
              disabled={isTicketPending}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select category"/>
              </SelectTrigger>
              <SelectContent>
                {Object.values(QA_CATEGORIES).map((value) => (
                  <SelectItem key={value} value={value}>
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        </div>

        {status === QA_STATUSES.CRITICAL && (
          <Alert variant="default" className="border-amber-500 bg-amber-50">
            <AlertDescription className="text-amber-900">
              <strong>Note:</strong> Our team reviews up to 10 critical tickets daily. If this issue is a
              duplicate or can wait, consider selecting "Medium priority" to help us prioritize effectively.
            </AlertDescription>
          </Alert>
        )}

        {
          canAddComment ? (
            <>
              <Textarea
                placeholder="Write a comment..."
                ref={addRef}
                rows={3}
                disabled={isTicketPending}
                onChange={(e) => setHasComment(!!e.target.value.trim())}
              />

              {isTicketCreated ? (
                <Button
                  onClick={handleAddComment}
                  className="w-full"
                  disabled={isTicketPending || !hasComment}
                >
                  <Send className="h-4 w-4 mr-2"/>
                  {isAddCommentPending ? 'Adding...' : 'Add Comment'}
                </Button>
              ) : (
                <Button
                  onClick={() => handleCreateTicket(addRef.current?.value.trim() ?? '')}
                  className="w-full"
                  disabled={isTicketPending || !status || !category || !hasComment}
                >
                  {isCreateTicketPending ? 'Creating...' : 'Create QA Ticket'}
                </Button>
              )}
            </>
          ) : (<p className="text-body text-gray-500">Maximum of {MAX_COMMENTS_PER_CALL} comments reached.</p>)
        }
      </div>

      {
        !!comments.length && (
          <div className="space-y-3">
            <h4 className="text-detail font-medium text-gray-700">Comments ({comments.length})</h4>

            {
              comments.map((comment) => {
                const isEditing = editingId === comment.id
                const isAuthor = comment.author_id === userId

                return (
                  <Card key={comment.id} className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-body font-medium text-gray-900">
                            {comment.author_name || 'Unknown'}
                          </span>
                          <span className="text-detail text-gray-500">{formatDate(comment.created_at)}</span>
                          {comment.created_at !== comment.updated_at && (
                            <span className="text-detail text-gray-400">(edited)</span>
                          )}
                        </div>
                        {isEditing ? (
                          <form className="space-y-2" onSubmit={(e) => handleUpdateComment(e, comment)}>
                            <Textarea
                              ref={editRef}
                              defaultValue={comment.message}
                              rows={3}
                              disabled={isCommentPending}
                            />
                            <div className="flex gap-2">
                              <Button size="sm" type="submit" disabled={isCommentPending}>
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                type="button"
                                onClick={handleCancelEdit}
                                disabled={isCommentPending}
                              >
                                Cancel
                              </Button>
                            </div>
                          </form>
                        ) : (
                          <p className="text-body text-gray-700 whitespace-pre-wrap">{comment.message}</p>
                        )}
                      </div>
                      {!isEditing && isAuthor && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleStartEdit(comment)}
                          className="h-8 w-8 p-0"
                          disabled={isCommentPending}
                        >
                          <Edit2 className="h-4 w-4"/>
                        </Button>
                      )}
                    </div>
                  </Card>
                )
              })
            }
          </div>
        )
      }

      {isSuperadmin && (
        <div className="pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            size="sm"
            onClick={handleImportAsTest}
            disabled={importFromQA.isPending}
          >
            <FlaskConical className="w-3.5 h-3.5 mr-1.5" />
            {importFromQA.isPending ? 'Creating...' : 'Create Simulation'}
          </Button>
        </div>
      )}
    </div>
  );
}
